import type { MapConfig, Player, Enemy, Weapon, GameState as GState, DifficultyProfile } from './types';
import { GameState } from './types';
import { Raycaster } from './raycaster';
import { Renderer } from './renderer';
import { Minimap } from './minimap';

// Configuration des maps
const maps: Record<string, MapConfig> = {
  classic: {
    id: 'classic',
    name: 'Classique',
    width: 16,
    height: 31,
    playerStart: { x: 3.5, y: 3.5 },
    data: [] // Sera chargé depuis JSON
  },
  arena: {
    id: 'arena',
    name: 'Arène',
    width: 18,
    height: 18,
    playerStart: { x: 9.5, y: 9.5 },
    data: []
  },
  maze: {
    id: 'maze',
    name: 'Labyrinthe',
    width: 20,
    height: 20,
    playerStart: { x: 10.5, y: 10.5 },
    data: []
  }
};

// Configuration des armes
const weapons: Record<string, Weapon> = {
  pistol: {
    name: 'Pistolet',
    damage: 25,
    cooldown: 240,
    spread: 0,
    pellets: 1,
    mode: 'semi',
    frames: [0]
  },
  shotgun: {
    name: 'Fusil à pompe',
    damage: 15,
    cooldown: 600,
    spread: 0.15,
    pellets: 7,
    mode: 'semi',
    frames: [0]
  },
  gatling: {
    name: 'Gatling',
    damage: 12,
    cooldown: 90,
    spread: 0.08,
    pellets: 1,
    mode: 'auto',
    frames: [0, 1, 2]
  }
};

// Profils de difficulté
const difficultyProfiles: Record<string, DifficultyProfile> = {
  easy: { enemySpeedMul: 0.85, waveEnemyMul: 0.85, damageTakenMul: 0.8 },
  normal: { enemySpeedMul: 1.0, waveEnemyMul: 1.0, damageTakenMul: 1.0 },
  hard: { enemySpeedMul: 1.25, waveEnemyMul: 1.25, damageTakenMul: 1.2 }
};

class Game {
  // Canvas
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Moteurs
  private raycaster: Raycaster;
  private renderer: Renderer;
  private minimap: Minimap;

  // État du jeu
  private gameState: GState = GameState.MENU;
  private isGameOver: boolean = false;
  private player: Player;
  private enemies: Enemy[] = [];
  private worldMap: number[] = [];
  private mapWidth: number = 0;
  private mapHeight: number = 0;
  private currentMap: MapConfig;
  private currentDifficulty: DifficultyProfile;
  private spawnTiles: { x: number; y: number }[] = [];

  // Gameplay
  private waveIndex: number = 1;
  private killCount: number = 0;
  private lastFireTime: number = 0;
  private currentWeapon: Weapon;
  private muzzleFlash: number = 0;
  private damageFlash: number = 0;

  // Input
  private keys: Map<string, boolean> = new Map();
  private mouseDeltaX: number = 0;
  private pointerLocked: boolean = false;

  // HUD Elements
  private hudHealth: HTMLElement | null;
  private hudKills: HTMLElement | null;
  private hudWave: HTMLElement | null;

  // Timing
  private lastTime: number = 0;
  private running: boolean = false;

  // Constantes gameplay
  private readonly BASE_ENEMIES = 3;
  private readonly GROWTH_FACTOR = 1.18;
  private readonly SPEED_BASE = 0.9;
  private readonly SPEED_GROWTH = 0.15;

  constructor(canvas: HTMLCanvasElement, minimapCanvas: HTMLCanvasElement) {
    this.canvas = canvas;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    this.ctx = ctx;

    // Initialiser le joueur
    this.player = {
      x: 8,
      y: 8,
      dirX: 1,
      dirY: 0,
      planeX: 0,
      planeY: 0.66,
      angle: 0,
      health: 100,
      hp: 100,
      maxHp: 100,
      baseSpeed: 2.8,
      moveSpeed: 2.8,
      rotSpeed: 2.4
    };

    // Map et difficulté par défaut
    this.currentMap = maps.classic;
    this.currentDifficulty = difficultyProfiles.normal;
    this.currentWeapon = weapons.pistol;

    // Initialiser les moteurs
    this.raycaster = new Raycaster();
    this.renderer = new Renderer(canvas);
    this.minimap = new Minimap(minimapCanvas);

    // HUD
    this.hudHealth = document.getElementById('hudHealth');
    this.hudKills = document.getElementById('hudKills');
    this.hudWave = document.getElementById('hudWave');

    this.setupCanvas();
    this.setupInput();
  }

  private setupCanvas(): void {
    this.canvas.width = 800;
    this.canvas.height = 600;
    this.renderer.resize(800, 600);
  }

  private setupInput(): void {
    // Clavier
    window.addEventListener('keydown', (e) => {
      this.keys.set(e.code, true);
      this.keys.set(e.key.toLowerCase(), true);
      
      // Armes
      if (e.code === 'Digit1') this.setWeapon(weapons.pistol);
      if (e.code === 'Digit2') this.setWeapon(weapons.shotgun);
      if (e.code === 'Digit3') this.setWeapon(weapons.gatling);

      // Pause
      if (e.code === 'Escape' || e.code === 'KeyP') {
        this.togglePause();
      }

      // Restart
      if (e.code === 'KeyR' && this.isGameOver) {
        this.restart();
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) {
        e.preventDefault();
      }
    });

    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
      this.keys.delete(e.key.toLowerCase());
    });

    // Souris
    this.canvas.addEventListener('click', () => {
      this.canvas.requestPointerLock();
    });

    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === this.canvas;
    });

    document.addEventListener('mousemove', (e) => {
      if (this.pointerLocked) {
        this.mouseDeltaX = e.movementX;
      }
    });

    // Molette pour changer d'arme
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.cycleWeapon(e.deltaY < 0 ? 1 : -1);
    }, { passive: false });
  }

  async loadMap(mapId: string): Promise<void> {
    try {
      const response = await fetch(`/assets/maps/${mapId}.json`);
      if (!response.ok) throw new Error(`Failed to load map: ${response.status}`);
      
      const data = await response.json();
      
      this.currentMap = maps[mapId] || maps.classic;
      this.currentMap.data = data.data;
      this.currentMap.width = data.width;
      this.currentMap.height = data.height;
      this.currentMap.playerStart = data.playerStart;

      this.mapWidth = this.currentMap.width;
      this.mapHeight = this.currentMap.height;
      this.worldMap = this.currentMap.data;

      this.raycaster.setMap(this.worldMap, this.mapWidth, this.mapHeight);
      this.minimap.resize({ w: this.mapWidth, h: this.mapHeight, tiles: this.worldMap, playerSpawn: { x: 0, y: 0, a: 0 } });
      this.rebuildSpawnTiles();

      console.log(`Map loaded: ${this.currentMap.name}`);
    } catch (error) {
      console.error('Error loading map:', error);
    }
  }

  start(): void {
    if (!this.running) {
      this.resetGame();
      this.running = true;
      this.gameState = GameState.PLAYING;
      this.lastTime = performance.now();
      requestAnimationFrame((time) => this.gameLoop(time));
    }
  }

  private resetGame(): void {
    this.player.x = this.currentMap.playerStart.x;
    this.player.y = this.currentMap.playerStart.y;
    this.player.dirX = 1;
    this.player.dirY = 0;
    this.player.planeX = 0;
    this.player.planeY = 0.66;
    this.player.health = 100;

    this.waveIndex = 1;
    this.killCount = 0;
    this.enemies = [];
    this.muzzleFlash = 0;
    this.damageFlash = 0;
    this.isGameOver = false;

    this.rebuildSpawnTiles();
    this.spawnWave();
    this.updateHUD();
  }

  restart(): void {
    this.resetGame();
    this.gameState = GameState.PLAYING;
    this.running = true;
    this.lastTime = performance.now();
  }

  private rebuildSpawnTiles(): void {
    this.spawnTiles = [];
    for (let y = 1; y < this.mapHeight - 1; y++) {
      for (let x = 1; x < this.mapWidth - 1; x++) {
        const idx = y * this.mapWidth + x;
        if (this.worldMap[idx] === 0) {
          this.spawnTiles.push({ x: x + 0.5, y: y + 0.5 });
        }
      }
    }
  }

  private spawnWave(): void {
    const raw = this.BASE_ENEMIES * Math.pow(this.GROWTH_FACTOR, Math.max(0, this.waveIndex - 1));
    let count = Math.round(raw * this.currentDifficulty.waveEnemyMul);
    count = Math.min(count, 18); // Cap

    const speedBase = Math.min(this.SPEED_BASE + (this.waveIndex - 1) * this.SPEED_GROWTH, 3.2);
    const speedMin = Math.max(0.36, speedBase);
    const speedMax = Math.min(3.2, speedBase + 0.7);

    // Utiliser le cache des spawn tiles
    if (this.spawnTiles.length === 0) {
      this.rebuildSpawnTiles();
    }

    const used = new Set<string>();
    
    for (let i = 0; i < count; i++) {
      let spawn: { x: number; y: number } | null = null;

      for (let j = 0; j < 25; j++) {
        const c = this.spawnTiles[Math.floor(Math.random() * this.spawnTiles.length)];
        if (!c) break;

        const key = `${Math.floor(c.x)}-${Math.floor(c.y)}`;
        const dx = c.x - this.player.x;
        const dy = c.y - this.player.y;
        const dist = Math.hypot(dx, dy);

        if (!used.has(key) && dist > 5) {
          spawn = c;
          used.add(key);
          break;
        }
      }

      if (spawn) {
        const speed = (speedMin + Math.random() * (speedMax - speedMin)) * this.currentDifficulty.enemySpeedMul;
        this.enemies.push({
          x: spawn.x,
          y: spawn.y,
          speed,
          alive: true,
          hp: 30,
          isBoss: false
        });
      }
    }

    console.log(`Wave ${this.waveIndex}: ${count} enemies`);
  }

  private gameLoop(time: number): void {
    if (!this.running) return;

    const dt = Math.min((time - this.lastTime) / 1000, 0.1);
    this.lastTime = time;

    if (this.gameState === GameState.PLAYING) {
      this.update(dt);
    }

    this.render();

    requestAnimationFrame((t) => this.gameLoop(t));
  }

  private update(dt: number): void {
    this.updatePlayer(dt);
    this.updateEnemies(dt);
    this.muzzleFlash = Math.max(0, this.muzzleFlash - dt * 10);
    this.damageFlash = Math.max(0, this.damageFlash - dt * 3);

    // Vérifier fin de vague
    if (this.enemies.filter(e => e.alive).length === 0) {
      this.waveIndex++;
      this.spawnWave();
      this.updateHUD();
    }

    // Game over
    if (this.player.health <= 0 && this.gameState === GameState.PLAYING) {
      this.gameState = GameState.GAME_OVER;
      this.isGameOver = true;
      console.log('Game Over! Score:', this.killCount);
    }
  }

  private updatePlayer(dt: number): void {
    const accel = this.keys.get('ShiftLeft') ? 1.8 : 1;
    const speed = this.player.baseSpeed * accel * dt;

    // Mouvement (compatible AZERTY/QWERTY)
    const forward = this.keys.get('z') || this.keys.get('KeyW') || this.keys.get('ArrowUp');
    const back = this.keys.get('s') || this.keys.get('KeyS') || this.keys.get('ArrowDown');
    const leftStrafe = this.keys.get('d') || this.keys.get('KeyD');
    const rightStrafe = this.keys.get('q') || this.keys.get('KeyA');

    if (forward) {
      const nx = this.player.x + this.player.dirX * speed;
      const ny = this.player.y + this.player.dirY * speed;
      if (this.raycaster.canMove(nx, this.player.y)) this.player.x = nx;
      if (this.raycaster.canMove(this.player.x, ny)) this.player.y = ny;
    }

    if (back) {
      const nx = this.player.x - this.player.dirX * speed;
      const ny = this.player.y - this.player.dirY * speed;
      if (this.raycaster.canMove(nx, this.player.y)) this.player.x = nx;
      if (this.raycaster.canMove(this.player.x, ny)) this.player.y = ny;
    }

    if (leftStrafe) {
      const nx = this.player.x - this.player.dirY * speed;
      const ny = this.player.y + this.player.dirX * speed;
      if (this.raycaster.canMove(nx, this.player.y)) this.player.x = nx;
      if (this.raycaster.canMove(this.player.x, ny)) this.player.y = ny;
    }

    if (rightStrafe) {
      const nx = this.player.x + this.player.dirY * speed;
      const ny = this.player.y - this.player.dirX * speed;
      if (this.raycaster.canMove(nx, this.player.y)) this.player.x = nx;
      if (this.raycaster.canMove(this.player.x, ny)) this.player.y = ny;
    }

    this.raycaster.pushOutOfWall(this.player);

    // Rotation
    const rotLeft = this.keys.get('a') || this.keys.get('KeyQ') || this.keys.get('ArrowLeft');
    const rotRight = this.keys.get('e') || this.keys.get('KeyE') || this.keys.get('ArrowRight');

    if (rotLeft) this.rotate(-this.player.rotSpeed * dt);
    if (rotRight) this.rotate(this.player.rotSpeed * dt);

    // Souris
    if (this.pointerLocked && this.mouseDeltaX !== 0) {
      this.rotate(this.mouseDeltaX * 0.002);
      this.mouseDeltaX = 0;
    }

    // Tir
    if (this.keys.get('Space')) {
      this.attemptShot();
    }

    // Auto-ouverture des portes proches
    this.checkNearbyDoors();
  }

  private checkNearbyDoors(): void {
    const doorOpenRadius = 1.2;
    const px = this.player.x;
    const py = this.player.y;
    const minTx = Math.floor(px - 1);
    const maxTx = Math.floor(px + 1);
    const minTy = Math.floor(py - 1);
    const maxTy = Math.floor(py + 1);

    for (let ty = minTy; ty <= maxTy; ty++) {
      for (let tx = minTx; tx <= maxTx; tx++) {
        if (tx < 0 || ty < 0 || tx >= this.mapWidth || ty >= this.mapHeight) continue;
        
        const idx = ty * this.mapWidth + tx;
        if (this.worldMap[idx] === 3) { // Porte fermée
          const cx = tx + 0.5;
          const cy = ty + 0.5;
          const dist = Math.hypot(px - cx, py - cy);
          
          if (dist <= doorOpenRadius) {
            this.raycaster.openDoor(tx, ty);
            console.log('Door opened');
          }
        }
      }
    }
  }

  private rotate(angle: number): void {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const oldDirX = this.player.dirX;
    this.player.dirX = this.player.dirX * cos - this.player.dirY * sin;
    this.player.dirY = oldDirX * sin + this.player.dirY * cos;

    const oldPlaneX = this.player.planeX;
    this.player.planeX = this.player.planeX * cos - this.player.planeY * sin;
    this.player.planeY = oldPlaneX * sin + this.player.planeY * cos;
  }

  private updateEnemies(dt: number): void {
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue;

      // Mouvement simple vers le joueur
      const dx = this.player.x - enemy.x;
      const dy = this.player.y - enemy.y;
      const dist = Math.hypot(dx, dy);

      if (dist > 0.5) {
        const moveX = (dx / dist) * enemy.speed * dt;
        const moveY = (dy / dist) * enemy.speed * dt;

        if (this.raycaster.canMove(enemy.x + moveX, enemy.y)) {
          enemy.x += moveX;
        }
        if (this.raycaster.canMove(enemy.x, enemy.y + moveY)) {
          enemy.y += moveY;
        }
      }

      // Attaque si proche
      if (dist < 0.8) {
        this.player.health -= 10 * this.currentDifficulty.damageTakenMul * dt;
        this.damageFlash = 1;
      }
    }
  }

  private attemptShot(): void {
    const now = performance.now();
    if (now - this.lastFireTime < this.currentWeapon.cooldown) return;

    this.lastFireTime = now;
    this.muzzleFlash = 1;

    // Hitscan simple
    for (let p = 0; p < this.currentWeapon.pellets; p++) {
      const spread = (Math.random() - 0.5) * this.currentWeapon.spread;
      const angle = Math.atan2(this.player.dirY, this.player.dirX) + spread;

      // Trouver l'ennemi le plus proche dans la ligne de tir
      let closestEnemy: Enemy | null = null;
      let closestDist = Infinity;

      for (const enemy of this.enemies) {
        if (!enemy.alive) continue;

        const dx = enemy.x - this.player.x;
        const dy = enemy.y - this.player.y;
        const dist = Math.hypot(dx, dy);

        const angleToEnemy = Math.atan2(dy, dx);
        const angleDiff = Math.abs(angleToEnemy - angle);

        if (angleDiff < 0.1 && dist < closestDist) {
          closestEnemy = enemy;
          closestDist = dist;
        }
      }

      if (closestEnemy) {
        closestEnemy.hp = (closestEnemy.hp || 30) - this.currentWeapon.damage;
        if (closestEnemy.hp <= 0) {
          closestEnemy.alive = false;
          this.killCount++;
          this.updateHUD();
        }
      }
    }
  }

  private render(): void {
    this.renderer.clear();
    this.renderer.drawBackground();

    // Raycasting
    const screenWidth = this.canvas.width;
    const rays = [];

    for (let x = 0; x < screenWidth; x++) {
      const cameraX = 2 * x / screenWidth - 1;
      const rayDirX = this.player.dirX + this.player.planeX * cameraX;
      const rayDirY = this.player.dirY + this.player.planeY * cameraX;

      const hit = this.raycaster.castRay(this.player, rayDirX, rayDirY);
      rays.push(hit);
    }

    // Rendu
    this.renderer.renderWalls(rays, screenWidth, this.canvas.height);
    this.renderer.renderSprites(
      this.enemies.filter(e => e.alive).map(e => ({
        type: 'enemy' as const,
        x: e.x,
        y: e.y,
        active: e.alive
      })),
      this.player,
      rays
    );

    // Flash
    if (this.muzzleFlash > 0) {
      this.ctx.fillStyle = `rgba(255, 200, 0, ${this.muzzleFlash * 0.3})`;
      this.ctx.fillRect(0, 0, screenWidth, this.canvas.height);
    }

    if (this.damageFlash > 0) {
      this.ctx.fillStyle = `rgba(255, 0, 0, ${this.damageFlash * 0.3})`;
      this.ctx.fillRect(0, 0, screenWidth, this.canvas.height);
    }

    // Minimap
    this.minimap.render(
      { w: this.mapWidth, h: this.mapHeight, tiles: this.worldMap, playerSpawn: { x: 0, y: 0, a: 0 } },
      this.player
    );

    // Crosshair
    if (this.gameState === GameState.PLAYING) {
      this.drawCrosshair();
    }

    // Overlays
    if (this.gameState === GameState.PAUSED) {
      this.drawPauseScreen();
    } else if (this.gameState === GameState.GAME_OVER) {
      this.showGameOver();
    }
  }

  private drawPauseScreen(): void {
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    this.ctx.fillStyle = '#fff';
    this.ctx.font = 'bold 48px monospace';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('PAUSE', this.canvas.width / 2, this.canvas.height / 2 - 20);
    
    this.ctx.font = '24px monospace';
    this.ctx.fillStyle = '#888';
    this.ctx.fillText('Appuyez sur ESC ou P pour continuer', this.canvas.width / 2, this.canvas.height / 2 + 40);
  }

  private drawCrosshair(): void {
    const centerX = this.canvas.width / 2;
    const centerY = this.canvas.height / 2;
    const size = 10;

    this.ctx.strokeStyle = '#0f0';
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(centerX - size, centerY);
    this.ctx.lineTo(centerX + size, centerY);
    this.ctx.moveTo(centerX, centerY - size);
    this.ctx.lineTo(centerX, centerY + size);
    this.ctx.stroke();
  }

  private updateHUD(): void {
    if (this.hudHealth) {
      this.hudHealth.textContent = `HP: ${Math.max(0, Math.floor(this.player.health))}`;
    }
    if (this.hudKills) {
      this.hudKills.textContent = `Kills: ${this.killCount}`;
    }
    if (this.hudWave) {
      this.hudWave.textContent = `Wave: ${this.waveIndex}`;
    }
  }

  private setWeapon(weapon: Weapon): void {
    this.currentWeapon = weapon;
    console.log(`Switched to: ${weapon.name}`);
  }

  private cycleWeapon(dir: number): void {
    const list = [weapons.pistol, weapons.shotgun, weapons.gatling];
    let idx = list.indexOf(this.currentWeapon);
    idx = (idx + dir + list.length) % list.length;
    this.setWeapon(list[idx]);
  }

  private togglePause(): void {
    if (this.isGameOver) return;
    
    if (this.gameState === GameState.PLAYING) {
      this.gameState = GameState.PAUSED;
      console.log('Game paused');
    } else if (this.gameState === GameState.PAUSED) {
      this.gameState = GameState.PLAYING;
      console.log('Game resumed');
    }
  }

  private showGameOver(): void {
    this.isGameOver = true;
    this.gameState = GameState.GAME_OVER;
    
    // Afficher le score final
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    this.ctx.fillStyle = '#ff0000';
    this.ctx.font = 'bold 48px monospace';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('GAME OVER', this.canvas.width / 2, this.canvas.height / 2 - 50);
    
    this.ctx.fillStyle = '#fff';
    this.ctx.font = '32px monospace';
    this.ctx.fillText(`Score: ${this.killCount}`, this.canvas.width / 2, this.canvas.height / 2 + 10);
    this.ctx.fillText(`Wave: ${this.waveIndex}`, this.canvas.width / 2, this.canvas.height / 2 + 50);
    
    this.ctx.font = '20px monospace';
    this.ctx.fillStyle = '#888';
    this.ctx.fillText('Appuyez sur R pour rejouer', this.canvas.width / 2, this.canvas.height / 2 + 100);
  }
}

// Point d'entrée
async function main() {
  const canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
  const minimapCanvas = document.getElementById('minimapCanvas') as HTMLCanvasElement;
  const startButton = document.getElementById('startButton') as HTMLButtonElement;

  if (!canvas || !minimapCanvas) {
    console.error('Canvas elements not found');
    return;
  }

  const game = new Game(canvas, minimapCanvas);

  // Charger la map de démo
  await game.loadMap('classic');

  // Démarrer le jeu
  if (startButton) {
    startButton.addEventListener('click', () => {
      game.start();
      startButton.style.display = 'none';
    });
  }

  console.log('Game ready!');
  console.log('Controls: ZQSD/WASD = Move, Mouse = Look, Space = Shoot, 1-3 = Weapons');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', main);
} else {
  main();
}
